import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import type { Contact } from '../../types';
import {
  createContact,
  updateContact,
  uploadContactPhoto,
  setContactPhotoUrl,
  getContactInitials,
} from '../../services/contacts';

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  contact?: Contact | null;
  onSaved: () => void;
}

export default function ContactDialog({ open, onClose, contact, onSaved }: ContactDialogProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nicknames, setNicknames] = useState<string[]>(['']);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Photo state
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const [showUrlField, setShowUrlField] = useState(false);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEdit = !!contact;

  // Populate form when dialog opens
  useEffect(() => {
    if (!open) return;
    if (contact) {
      setFirstName(contact.firstName || '');
      setLastName(contact.lastName || '');
      setNicknames(contact.nicknames.length > 0 ? [...contact.nicknames] : ['']);
      setEmail(contact.email || '');
      setPhone(contact.phone || '');
      setPhotoPreview(contact.photoUrl);
    } else {
      setFirstName('');
      setLastName('');
      setNicknames(['']);
      setEmail('');
      setPhone('');
      setPhotoPreview(null);
    }
    setPhotoFile(null);
    setPhotoUrlInput('');
    setShowUrlField(false);
    setPhotoRemoved(false);
  }, [open, contact]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoUrlInput('');
    setShowUrlField(false);
    setPhotoRemoved(false);
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoUrlInput('');
    setShowUrlField(false);
    setPhotoRemoved(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let contactId = contact?.id;

      const filteredNicknames = [...new Set(nicknames.map(n => n.trim()).filter(Boolean))];
      const data: Record<string, any> = {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        nicknames: filteredNicknames.length > 0 ? filteredNicknames : undefined,
        email: email || undefined,
        phone: phone || undefined,
      };

      if (isEdit && contactId) {
        // If photo removed, include photoUrl: null in update
        if (photoRemoved) {
          (data as any).photoUrl = null;
        }
        await updateContact(contactId, data);
      } else {
        const newContact = await createContact(data);
        contactId = newContact.id;
      }

      // Handle photo upload/url after contact exists
      if (contactId) {
        if (photoFile) {
          await uploadContactPhoto(contactId, photoFile);
        } else if (photoUrlInput.trim()) {
          await setContactPhotoUrl(contactId, photoUrlInput.trim());
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save contact:', err);
    } finally {
      setSaving(false);
    }
  };

  // Build initials for avatar fallback
  const initials = contact
    ? getContactInitials(contact)
    : firstName
      ? (firstName[0] + (lastName ? lastName[0] : '')).toUpperCase()
      : '?';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={mobile}>
      <DialogTitle>{isEdit ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
      <DialogContent>
        {/* Photo area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, mt: 1 }}>
          <Avatar
            src={photoPreview || undefined}
            sx={{ width: 80, height: 80, fontSize: 32, mb: 1 }}
          >
            {initials}
          </Avatar>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => fileInputRef.current?.click()}>
              Upload Photo
            </Button>
            <Button size="small" onClick={() => setShowUrlField((v) => !v)}>
              Set URL
            </Button>
            {(photoPreview || contact?.photoUrl) && !photoRemoved && (
              <Button size="small" color="error" onClick={handleRemovePhoto}>
                Remove Photo
              </Button>
            )}
          </Stack>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileSelect}
          />
          {showUrlField && (
            <TextField
              label="Photo URL"
              size="small"
              fullWidth
              value={photoUrlInput}
              onChange={(e) => {
                setPhotoUrlInput(e.target.value);
                if (e.target.value) {
                  setPhotoPreview(e.target.value);
                  setPhotoFile(null);
                  setPhotoRemoved(false);
                }
              }}
              sx={{ mt: 1, maxWidth: 400 }}
            />
          )}
        </Box>

        {/* Form fields */}
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
            />
          </Stack>
          {nicknames.map((nick, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField
                label={`Nickname ${i + 1}`}
                value={nick}
                onChange={(e) => {
                  const updated = [...nicknames];
                  updated[i] = e.target.value;
                  setNicknames(updated);
                }}
                fullWidth
                disabled={contact?.nicknames.includes('me') && nick === 'me'}
              />
              {nicknames.length > 1 && !(contact?.nicknames.includes('me') && nick === 'me') && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setNicknames(nicknames.filter((_, j) => j !== i))}
                  sx={{ minWidth: 'auto' }}
                >
                  Remove
                </Button>
              )}
            </Stack>
          ))}
          {nicknames.length < 5 && (
            <Button size="small" onClick={() => setNicknames([...nicknames, ''])}>
              Add Nickname
            </Button>
          )}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={22} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
